import { FieldBlock } from '@orbind/ui'
import { FormSection } from '../../components/FormScreen.tsx'
import { CampoInput, camposDoGrupo, rotuloCampo } from '../work-item/campos-shared.tsx'
import { useCustomFields } from './custom-field-data.ts'
import './campos-da-entidade.css'

/**
 * Campos complementares de EMPRESA e PESSOA (fase 6 da SPEC-20260828-1914).
 *
 * Quem liga o campo à entidade é o TÓPICO: cada tópico de `company`/`person`
 * vira uma seção do formulário de cadastro, com os campos que foram colocados
 * nele. Não há "tipo" aqui — tipo só existe no Item de Trabalho.
 *
 * Diferente do detalhe do Item, aqui NÃO se grava campo a campo: os valores
 * ficam no estado do formulário e vão junto no Salvar, como qualquer outro
 * campo do cadastro.
 */
export function CamposDaEntidade({
  entity,
  valores,
  onMudar,
}: {
  entity: 'company' | 'person'
  valores: Record<string, unknown>
  onMudar: (valores: Record<string, unknown>) => void
}) {
  const dados = useCustomFields(entity)

  const campos = (dados.data?.fields ?? []).filter((f) => f.defaultVisible)
  const grupos = dados.data?.groups ?? []
  const placements = dados.data?.placements ?? []

  /** Campos que já estão colocados em algum lugar — o layout mandou neles. */
  const colocados = new Set(placements.map((p) => p.fieldId))

  /**
   * Campos de UM tópico. Quem manda é a COLOCAÇÃO: é ela que carrega largura e
   * ordem, e é o que a tela de administração desenha. O `groupId` do campo entra
   * como rede de segurança para campos criados fora do construtor.
   *
   * Só entra no formulário o que está num tópico. Campo que existe no catálogo
   * mas não foi colocado em lugar nenhum NÃO aparece — senão todo campo novo
   * cairia no cadastro sem ninguém pedir.
   */
  const doGrupo = (gid: string) => {
    const porColocacao = camposDoGrupo(campos, placements, gid)
    const semColocacao = campos
      .filter((f) => f.groupId === gid && !colocados.has(f.id))
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((campo) => ({ campo, width: 12, placement: null }))
    return [...porColocacao, ...semColocacao]
  }

  // um tópico só aparece quando tem campo dentro dele
  const topicos = grupos.map((g) => ({ grupo: g, itens: doGrupo(g.id) })).filter((t) => t.itens.length > 0)

  if (topicos.length === 0) return null

  const secao = (titulo: string, itens: ReturnType<typeof doGrupo>, chave: string) => (
    <FormSection key={chave} title={titulo}>
      <div className="cent__grade">
        {itens.map(({ campo, width }) => (
          <div key={campo.id} className="cent__campo" style={{ ['--cent-w' as string]: String(width) }}>
            <FieldBlock label={rotuloCampo(campo.labels, campo.key)} required={campo.required}>
              {() => (
                <CampoInput
                  f={campo}
                  valores={valores}
                  setValores={(fn) => onMudar(fn(valores))}
                  // sem gravação por campo: o Salvar do formulário é que persiste
                  gravar={() => undefined}
                  // aqui a altura vale para a entidade, não para um registro:
                  // Empresa/Pessoa abrem sempre no mesmo formulário
                  escopo={entity}
                />
              )}
            </FieldBlock>
          </div>
        ))}
      </div>
    </FormSection>
  )

  return (
    <>
      {topicos.map((t) =>
        secao(rotuloCampo(t.grupo.labels, 'Complementares'), t.itens, t.grupo.id),
      )}
    </>
  )
}
